import * as React from 'react'

import {
  Alert,
  Box,
  Spinner,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { camelCase } from 'lodash'

import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import LevelSelect from 'modules/employees/components/UnifiedAddEmployeeModal/shared/LevelSelect'
import RolesSelect from 'modules/employees/components/UnifiedAddEmployeeModal/shared/RolesSelect'
import useCreateRole from 'modules/employees/components/UnifiedAddEmployeeModal/shared/useCreateRole'
import { Fields } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/Fields'
import { MultiTeamField } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/MultiTeamField'
import {
  WizardData,
  genericAmplitudeProperties,
} from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { useLegalEntityRoles } from 'modules/employees/hooks/useLegalEntityRoles'
import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'
import { useWorkAreas } from 'modules/locations/hooks/useWorkAreas'
import { useFinancialWorkspaceSeePayroll } from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceSeePayroll'
import JobCatalogTreeSelector from 'modules/job_catalog/components/JobCatalogTreeSelector'
import useCreateLevel from 'modules/job_catalog/hooks/useCreateLevel'

interface State {
  isOpenLevelSelector: boolean
  isOpenRoleSelector: boolean
  showRoleError: boolean
}

const WorkDetailsStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [managers, setManagers] = React.useState<Record<string, string | undefined>>({
    managerId: data.managerId ? data.managerId : undefined,
    timeoffManagerId: data.timeoffManagerId ? data.timeoffManagerId : undefined,
  })
  const [state, setState]: [State, React.Dispatch<React.SetStateAction<State>>] =
    React.useState<State>({
      isOpenLevelSelector: false,
      isOpenRoleSelector: false,
      showRoleError: false,
    })
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const fields = data.fieldsSchema?.filter((field) => field.category === 'work_details')
  const filterFields = fields?.filter(
    (field) =>
      (field.fieldName !== 'manager_id' && field.fieldName !== 'timeoff_manager_id') ||
      data.canAssignManager
  )
  // Separate team_id field for multi-select handling
  const teamField = filterFields?.find((field) => field.fieldName === 'team_id')
  const workAssignmentFields = filterFields?.filter(
    (field) => field.section === 'work_assignment' && field.fieldName !== 'team_id'
  )
  const reportingFields = filterFields?.filter((field) => field.section === 'reporting')

  const locationIdForWorkAreas = data.locationId ? data.locationId : undefined
  const { workAreas, loading: loadingWorkAreas } = useWorkAreas({
    locationIds: locationIdForWorkAreas !== undefined ? [locationIdForWorkAreas] : undefined,
    onlyNonArchived: true,
  })

  useUnsafeEffect(() => {
    if (
      data.locationId === undefined ||
      (data.locationId === '' && data.defaultWorkAreaId !== undefined)
    ) {
      onDataChanged({ ...data, defaultWorkAreaId: undefined })
    }
  }, [data.locationId, data.defaultWorkAreaId])

  const [rolesCollection, isLoadingRoles, refetchRoles, canSeeJobCatalog, canCreateJob] =
    useLegalEntityRoles(data.legalEntityId ? data.legalEntityId : data.legalEntity?.id)
  const [createRole] = useCreateRole()
  const isRoleSelected = !!data.roleId
  const role = rolesCollection && data.roleId ? rolesCollection[data.roleId] : undefined
  const levels = React.useMemo(() => {
    return role && rolesCollection ? (rolesCollection[role.id]?.levels ?? []) : []
  }, [role, rolesCollection])
  const isLevelSelected = data.jobLevelId
  const roleHasLevels = React.useMemo(() => {
    return levels && levels.filter((level) => !level.isDefault).length > 0
  }, [levels])
  const createLevel = useCreateLevel()

  const onCreateRole = async (name: string) => {
    if (data?.legalEntityId) {
      const result = await createRole(name, companyId, data.legalEntityId)
      const handled = handleResult(result, () => {
        setState({ ...state, showRoleError: true })
      })

      if (handled.success) {
        onDataChanged({ ...data, roleId: handled.data.role?.id?.toString() })
        refetchRoles()
      }
    }
  }

  const handleCreateLevel = async (name: string) => {
    if (role) {
      const result = await createLevel(name, role.id)
      const handled = handleResult(result, () => {})

      if (handled.success) {
        onDataChanged({
          ...data,
          jobLevelId: handled.data.level?.id?.toString(),
        })
        refetchRoles()
      }
    }
  }

  const handleSelect = (fieldName: string, employeeId: string | undefined) => {
    setManagers((prevManagers) => ({
      ...prevManagers,
      [fieldName]: employeeId,
    }))

    onDataChanged({
      ...data,
      managerId: fieldName === 'managerId' ? (employeeId ?? '') : data.managerId,
      timeoffManagerId:
        fieldName === 'timeoffManagerId' ? (employeeId ?? '') : data.timeoffManagerId,
    })
  }

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
  }

  const onTeamsChange = (teamIds: string[]) => {
    onDataChanged({
      ...data,
      teamIds,
      // Keep teamId in sync with first selected team for backward compatibility
      teamId: teamIds[0] || undefined,
    })
  }

  const hasPayrollField = fields?.find((f) => f.fieldName === 'payroll_policy_id')

  const { canSeeFinanceWorkspacePayroll } = useFinancialWorkspaceSeePayroll()

  const onRoleSelect = (selectedRoleId: string | undefined) => {
    setState({ ...state, isOpenRoleSelector: false })

    // If role has no custom levels, set the default level immediately
    const selectedRole = selectedRoleId ? rolesCollection?.[selectedRoleId] : undefined
    const selectedRoleLevels = selectedRole ? rolesCollection?.[selectedRole.id]?.levels : undefined
    const hasCustomLevels =
      selectedRoleLevels && selectedRoleLevels.filter((level) => !level.isDefault).length > 0

    let jobLevelId = undefined
    if (selectedRoleId && !hasCustomLevels) {
      const defaultLevelId = selectedRoleLevels
        ?.find((roleLevel) => roleLevel.isDefault)
        ?.id?.toString()
      jobLevelId = defaultLevelId
    }

    onDataChanged({ ...data, roleId: selectedRoleId, jobLevelId })
  }

  const onJobCatalogTreeLevelSelect = (
    jobCatalogTreeNodeUuid: string | undefined,
    roleLevelName?: string
  ) => {
    // Prevent infinite loop by checking if values have actually changed
    if (
      data.jobCatalogTreeNodeUuid === jobCatalogTreeNodeUuid &&
      data.roleLevelName === roleLevelName
    ) {
      return
    }

    onDataChanged({ ...data, jobCatalogTreeNodeUuid, roleLevelName })
  }

  const onLevelSelect = (levelId: string | undefined) => {
    onDataChanged({ ...data, jobLevelId: levelId })
    setState({ ...state, isOpenLevelSelector: false })
  }

  const onToggleRoleSelector = (isOpen: boolean) => {
    setState({ ...state, isOpenRoleSelector: isOpen, showRoleError: false })
  }

  const onToggleLevelSelector = (isOpen: boolean) => {
    setState({ ...state, isOpenLevelSelector: isOpen })
  }

  useOnNext(() => {
    track(unifiedAddEmployeeEvents.addWorkDetails, genericAmplitudeProperties(data))
  })

  if (isLoadingRoles) {
    return (
      <Box flexGrow justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.work_conditions_step.section.work_assignment')}
          </Text.Header2>
          {data.hasDevJobCatalogTree && canSeeJobCatalog && (
            <Box flexDirection="row" gap="s16">
              <JobCatalogTreeSelector
                serializedTree={data.jobCatalogTrees?.[0]?.serializedTree ?? ''}
                selectedLevelId={data.jobCatalogTreeNodeUuid}
                onLevelSelect={onJobCatalogTreeLevelSelect}
              />
            </Box>
          )}
          {!data.hasDevJobCatalogTree && data.hasJobCatalog && canSeeJobCatalog && (
            <Box flexDirection="row" gap="s16">
              <RolesSelect
                onRoleSelect={onRoleSelect}
                selectedRole={data.roleId}
                roles={Object.values(rolesCollection || {})}
                createJob={onCreateRole}
                onToggle={onToggleRoleSelector}
                error={
                  state.showRoleError
                    ? i18n.t('employee_form.wizard.role_step.role_selector.error_no_level')
                    : undefined
                }
                isOpen={state.isOpenRoleSelector}
                canCreate={canCreateJob}
              />
              <LevelSelect
                onLevelSelect={onLevelSelect}
                selectedLevel={data.jobLevelId}
                levels={roleHasLevels ? levels : []}
                disabled={!isRoleSelected || (!roleHasLevels && !canCreateJob)}
                isOpen={state.isOpenLevelSelector}
                onToggle={onToggleLevelSelector}
                noLevels={isRoleSelected && !roleHasLevels}
                createLevel={handleCreateLevel}
                mandatory={isRoleSelected && !!roleHasLevels && !isLevelSelected}
                canCreate={canCreateJob}
              />
            </Box>
          )}
          {teamField && (
            <MultiTeamField
              dataSchemaItem={teamField}
              values={data.teamIds || (data.teamId ? [data.teamId] : [])}
              onChange={onTeamsChange}
              mandatory={teamField.required}
              legalEntityId={data.legalEntityId || undefined}
            />
          )}
          {workAssignmentFields?.map((field) => (
            <Fields key={field.fieldName} field={field} data={data} onChangeField={onChangeField} />
          ))}
          <Box flexDirection="column" gap="s8">
            <Selector.Popover
              blankLabel={i18n.t(
                'add_employee_modal.work_conditions_step.default_workarea_placeholder'
              )}
              placeholder={i18n.t(
                'add_employee_modal.work_conditions_step.default_workarea_placeholder'
              )}
              searchPlaceholder={i18n.t(
                'add_employee_modal.work_conditions_step.default_workarea_label'
              )}
              inputStyle="badge"
              label={i18n.t('add_employee_modal.work_conditions_step.default_workarea_label')}
              onChange={(newValue) =>
                onDataChanged({
                  ...data,
                  defaultWorkAreaId: newValue ? String(newValue) : undefined,
                })
              }
              value={data.defaultWorkAreaId ? [data.defaultWorkAreaId] : []}
              isMultiSelect={false}
              mandatory={false}
              isDisabled={
                loadingWorkAreas ||
                !workAreas.length ||
                data.locationId === undefined ||
                data.locationId === ''
              }
            >
              {workAreas.map((workArea) => (
                <Selector.Option
                  key={workArea.id}
                  value={String(workArea.id)}
                  label={workArea.name}
                  searchValue={workArea.name}
                >
                  {workArea.name}
                </Selector.Option>
              ))}
            </Selector.Popover>
          </Box>
        </Box>
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.work_conditions_step.section.reporting')}
          </Text.Header2>
          {reportingFields?.map((field) => (
            <Fields
              key={field.fieldName}
              field={field}
              data={data}
              onChangeField={onChangeField}
              handleEmployeeSelect={handleSelect}
              employeesValue={managers[camelCase(field.fieldName)]}
              includeInactive={false}
            />
          ))}
        </Box>
      </Box>

      <Box flexDirection="column" paddingTop="s24" paddingRight="s4" maxWidth="s240" gap="s16">
        {data.hasDevJobCatalogTree && (
          <Box paddingTop="s16">
            <Alert color="note">
              <Text.Normal whiteSpace="breakSpaces">
                {i18n.t('employee_form.wizard.role_step.job_catalog_tree.note.description')}
              </Text.Normal>
            </Alert>
          </Box>
        )}
        {!data.hasDevJobCatalogTree && data.hasJobCatalog && canSeeJobCatalog && (
          <>
            <Box paddingTop="s16">
              {!isRoleSelected && !state.isOpenRoleSelector && (
                <Alert color="note">{i18n.t('employee_form.wizard.role_step.role_alert')}</Alert>
              )}
              {!isRoleSelected && state.isOpenRoleSelector && (
                <Alert color="info">
                  {i18n.t('employee_form.wizard.role_step.add_role_alert')}
                </Alert>
              )}
            </Box>
            <Box paddingTop="s16">
              {isRoleSelected && isRoleSelected && !isLevelSelected && roleHasLevels && (
                <Alert color="warning">
                  🚨 {i18n.t('employee_form.wizard.role_step.level_alert')}
                </Alert>
              )}
              {isRoleSelected && !isLevelSelected && !roleHasLevels && (
                <Alert color="info">
                  {i18n.t('employee_form.wizard.role_step.add_level_alert')}
                </Alert>
              )}
              {isRoleSelected && !roleHasLevels && !canCreateJob && (
                <Alert color="note">
                  {i18n.t('employee_form.wizard.role_step.not_level_alert')}
                </Alert>
              )}
            </Box>
          </>
        )}
        <Box paddingTop="s240" height="s140">
          {hasPayrollField && canSeeFinanceWorkspacePayroll && (
            <Alert color="info">{i18n.t('finance.financial_workspace.launch_info.payroll')}</Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const workDetailsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.work_conditions_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <WorkDetailsStep {...props} />,
  isCompleted: () => true,
})
